import type { ElementSpec } from './render.js';

/**
 * The only part of the widget that touches the DOM.
 *
 * Deliberately mechanical: it walks a spec and builds nodes. Every decision
 * about what appears has already been made in `view-model.ts` and `render.ts`,
 * both of which are plain functions over plain data and are where the tests
 * are. What is left here is the translation.
 *
 * The line that matters is `textContent`. The assistant's reply comes from a
 * language model; it has been screened, and it is still not markup. Nothing
 * below parses a string, and `ElementSpec` has no field that could carry one.
 */

/**
 * The DOM, as much of it as this widget uses.
 *
 * Declared here rather than by adding `"DOM"` to the compiler's `lib`. That
 * would hand browser globals to every package in the repository, including the
 * server ones, where `document` existing in the type system is an invitation.
 * Writing the surface out has two further benefits: it is a short and honest
 * statement of everything the widget needs from a host page, and it means the
 * binder can be tested against a stub instead of a browser.
 */
export interface DomNode {
  className: string;
  textContent: string | null;
  setAttribute(name: string, value: string): void;
  /**
   * Needed because the binder now updates nodes instead of rebuilding them.
   *
   * When a repaint drops an attribute the old node still carries it, and a
   * `disabled` or `aria-expanded` left behind is a control that lies about
   * itself. Replacing the tree used to make this impossible; patching it makes
   * it the one thing that can go wrong silently.
   */
  removeAttribute(name: string): void;
  appendChild(child: DomNode): unknown;
  replaceChildren(...children: DomNode[]): void;
}

export interface DomDocument {
  createElement(tag: string): DomNode;
  /**
   * Optional so the binder can still be driven by a twenty-line stub.
   *
   * Real SVG needs it: an `<svg>` built with `createElement` is an unknown HTML
   * element and renders nothing at all, which is a failure that looks like a
   * missing icon rather than like a bug.
   */
  createElementNS?(namespace: string, tag: string): DomNode;
}

export function createNode(spec: ElementSpec, doc: DomDocument): DomNode {
  return build(spec, doc).node;
}

/**
 * A node, the spec it was drawn from, and the same for everything inside it.
 *
 * Kept so the next paint can be a comparison rather than a demolition. The
 * previous *spec* is what makes patching cheap and safe: it says exactly which
 * attributes existed last time, so the binder never has to read the DOM back,
 * and the whole surface it needs from a host page stays the seven members of
 * `DomNode`.
 */
export interface PaintedTree {
  readonly node: DomNode;
  readonly spec: ElementSpec;
  readonly children: readonly PaintedTree[];
}

function build(spec: ElementSpec, doc: DomDocument): PaintedTree {
  // Namespaces are inherited: `<path>` inside an `<svg>` is an SVG path, and
  // making every child of an icon repeat the namespace would be one more thing
  // to forget in exactly the place nobody looks.
  const node =
    spec.ns === undefined
      ? doc.createElement(spec.tag)
      : (doc.createElementNS?.(spec.ns, spec.tag) ?? doc.createElement(spec.tag));

  if (spec.className !== undefined) setClassName(node, spec, spec.className);

  for (const [name, value] of Object.entries(spec.attrs ?? {})) {
    node.setAttribute(name, value);
  }

  // Never innerHTML, and there is deliberately no way to ask for it.
  if (spec.text !== undefined) node.textContent = spec.text;

  const children = (spec.children ?? []).map((child) => build(child, doc));
  for (const child of children) node.appendChild(child.node);

  return { node, spec, children };
}

/**
 * `className` on an SVG element is an SVGAnimatedString and read-only —
 * assigning to it throws in strict mode and silently does nothing otherwise.
 * The attribute is the same thing by another name and works on both, but only
 * HTML gets the property so the fast path stays.
 */
function setClassName(node: DomNode, spec: ElementSpec, value: string): void {
  if (spec.ns === undefined) node.className = value;
  else node.setAttribute('class', value);
}

/**
 * Update a painted node in place, or say it cannot be done.
 *
 * Returns `null` when the shape changed — a different tag or namespace — which
 * is the caller's signal to build a replacement. Everything else is a write
 * only where the two specs disagree, so a repaint that changes one price
 * touches one text node and leaves several hundred others, and every animation
 * running on them, alone.
 */
function patch(painted: PaintedTree, next: ElementSpec, doc: DomDocument): PaintedTree | null {
  const previous = painted.spec;
  if (next.tag !== previous.tag || next.ns !== previous.ns) return null;

  const node = painted.node;

  if (next.className !== previous.className) setClassName(node, next, next.className ?? '');

  const before = previous.attrs ?? {};
  const after = next.attrs ?? {};
  for (const [name, value] of Object.entries(after)) {
    if (before[name] !== value) node.setAttribute(name, value);
  }
  for (const name of Object.keys(before)) {
    if (!(name in after)) node.removeAttribute(name);
  }

  const retext = next.text !== previous.text;

  const wanted = next.children ?? [];
  const held = painted.children;
  let restructured = retext || wanted.length !== held.length;
  const children: PaintedTree[] = [];

  for (const [index, spec] of wanted.entries()) {
    const reusable = retext ? undefined : held[index];
    const patched = reusable === undefined ? null : patch(reusable, spec, doc);
    if (patched === null) {
      restructured = true;
      children.push(build(spec, doc));
    } else {
      children.push(patched);
    }
  }

  /**
   * Content is placed in one go, and only when something about it changed.
   *
   * Text and children cannot be written as two independent halves, which is
   * the bug this shape exists to prevent. In the DOM a node's text *is* one of
   * its children: assigning `textContent` removes every child, and
   * `replaceChildren()` with nothing removes the text. Setting the text first
   * and then calling `replaceChildren` — the obvious order — therefore erased
   * the text again on every leaf, which is every price, every label and every
   * word in the panel. It shipped, because a fake DOM keeps text in a field
   * beside its children and neither call disturbed the other.
   *
   * The rest of the time nothing is written here at all. A node that leaves the
   * document and comes back loses its scroll offset and restarts its
   * animations — the very things this binder exists to preserve — so the common
   * repaint, where every child patched in place and the text held, does not
   * touch the node's contents.
   */
  if (retext || restructured) {
    node.textContent = next.text ?? null;
    for (const child of children) node.appendChild(child.node);
  }

  return { node, spec: next, children };
}

/**
 * Draw a spec into a host, reusing whatever the last paint left there.
 *
 * Pass back the tree this returns and the next call becomes a diff. Without it
 * — or when the root's shape has changed — the host is replaced wholesale,
 * which is what this did for its whole life and is still the right answer for
 * the first paint.
 *
 * The reason it is worth the machinery: `replaceChildren` hands every node in
 * the panel to the compositor as a new one, so every animation attached to any
 * of them starts again and every scroll container inside it returns to the top.
 * A bettor who opened the quick options saw the panel appear to reload under
 * them. Nothing was wrong with what was drawn; the cost was in drawing it all
 * again.
 */
export function paint(
  host: DomNode,
  spec: ElementSpec,
  doc: DomDocument,
  previous: PaintedTree | null = null,
): PaintedTree {
  if (previous !== null) {
    const patched = patch(previous, spec, doc);
    if (patched !== null) return patched;
  }

  const built = build(spec, doc);
  host.replaceChildren(built.node);
  return built;
}
